import { useCallback, useContext, useMemo } from 'react';
import {
	PRODUCT_DISCOVERY_PROJECT,
	SERVICE_DESK_PROJECT,
	toProjectType,
} from '@jira/platform__project-types/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import {
	isIssueEmbedPreviewPanelOpen,
	useIsNativeIssueViewPreviewPanel,
} from '@atlassian/jira-preview-panels/src/useIsIssuePreviewPanelOpen.tsx';
import { JiraPreviewPanelsContext } from '@atlassian/jira-preview-panels/src/PreviewPanelsProvider.tsx';
import { useProject } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/project/index.tsx';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import { createAri } from '@atlassian/jira-platform-ari/src/index.tsx';
import { createRouterSelector, useQueryParam } from '@atlassian/react-resource-router';

// eslint-disable-next-line @typescript-eslint/no-invalid-void-type
export const useIsFullPageIssueView: (args_0: void) => boolean = createRouterSelector<boolean>(
	({ route }) => {
		return route?.name === 'issue';
	},
);

export const useGetIssueAriId = (issueId?: string): string => {
	const cloudId = useCloudId();
	const issueAriId = useMemo(() => {
		if (!issueId || !cloudId) return '';
		return createAri({
			resourceOwner: 'jira',
			cloudId,
			resourceType: 'issue',
			resourceId: issueId,
		});
	}, [issueId, cloudId]);
	return issueAriId;
};

export const useShouldShowIframeIssuePreviewPanel = (): (() => boolean) => {
	const isFullPageIssueView = useIsFullPageIssueView();
	const isPreviewPanelContextAvailable = !!useContext(JiraPreviewPanelsContext);
	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);
	const isJSMOrJPDProject =
		projectType === SERVICE_DESK_PROJECT || projectType === PRODUCT_DISCOVERY_PROJECT;

	const shouldShowIframePreviewPanel = useCallback(() => {
		const isIssueEmbedPreviewPanel = isIssueEmbedPreviewPanelOpen();

		if (
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('issue_preview_panel_issue_view_subtask') &&
			fg('jira_preview_panel_m_1_rollout') &&
			fg('jira-glance-panel') &&
			isPreviewPanelContextAvailable &&
			(fg('jsm_enable_preview_panel') || !isJSMOrJPDProject)
		) {
			return (
				(isFullPageIssueView && fg('gpp_child_linked_full_page_issue_view')) ||
				isIssueEmbedPreviewPanel
			);
		}
		return false;
	}, [isPreviewPanelContextAvailable, isJSMOrJPDProject, isFullPageIssueView]);

	return shouldShowIframePreviewPanel;
};

export const useShouldShowNativeIssuePreviewPanel = (): (() => boolean) => {
	const isPreviewPanelContextAvailable = !!useContext(JiraPreviewPanelsContext);
	const { data: project } = useProject();
	const projectType = toProjectType(project?.type);
	const isJSMOrJPDProject =
		projectType === SERVICE_DESK_PROJECT || projectType === PRODUCT_DISCOVERY_PROJECT;
	const isIssueNativePreviewPanel = useIsNativeIssueViewPreviewPanel();

	const shouldShowNativeIssuePreviewPanel = useCallback(() => {
		if (
			// eslint-disable-next-line jira/ff/no-preconditioning
			fg('issue_preview_panel_issue_view_subtask') &&
			fg('jira_preview_panel_m_1_rollout') &&
			fg('jira-glance-panel') &&
			isPreviewPanelContextAvailable &&
			(fg('jsm_enable_preview_panel') || !isJSMOrJPDProject)
		) {
			return isIssueNativePreviewPanel;
		}
		return false;
	}, [isPreviewPanelContextAvailable, isJSMOrJPDProject, isIssueNativePreviewPanel]);

	return shouldShowNativeIssuePreviewPanel;
};

export const useOpenNativeIssuePreviewPanel = (): ((issueKey?: string) => void) => {
	const [, setSelectedIssue] = useQueryParam('selectedIssue');
	const openNativeIssuePreviewPanel = useCallback(
		(issueKey?: string) => {
			if (!issueKey) return;
			setSelectedIssue(issueKey);
		},
		[setSelectedIssue],
	);
	return openNativeIssuePreviewPanel;
};
